<div class="container">
  <header class="header">
    <button class="back-button" (click)="goBack()">
      <span class="back-arrow">←</span>
    </button>
    <h1 class="header-text">Standards Against Child Sexual Abuse and Exploitation (CSAE)</h1>
  </header>

  <div class="content">
    <p class="last-updated">Last Updated: {{ lastUpdated }}</p>

    <section class="section">
      <h2 class="heading">Our Commitment</h2>
      <p class="paragraph">
        Clocc That Tea is committed to creating a safe, respectful, and age-appropriate environment for all users. We have zero tolerance for child sexual abuse and exploitation (CSAE) in any form. We are dedicated to preventing, detecting, and responding to any content or behavior that may harm minors.
      </p>
    </section>

    <section class="section">
      <h2 class="heading">Age Restrictions and Verification</h2>

      <h3 class="sub-heading">Minimum Age Requirement</h3>
      <p class="paragraph">
        <b>Users must be 18 years or older</b> to create an account and use Clocc That Tea.
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">We strictly enforce this age requirement through our registration process</li>
        <li class="bullet-point">Age verification is required during account creation and profile setup</li>
      </ul>

      <h3 class="sub-heading">Age Verification Methods</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Self-Declaration:</b> Users must declare they are 18 years or older during registration</li>
        <li class="bullet-point"><b>Phone Verification:</b> All users must verify their phone number, which helps ensure account authenticity</li>
        <li class="bullet-point"><b>Email Verification:</b> Email verification is required for all accounts</li>
      </ul>

      <h3 class="sub-heading">Age Restrictions Enforcement</h3>
      <ul class="bullet-list">
        <li class="bullet-point">Users under 18 years of age are prohibited from using our platform</li>
        <li class="bullet-point">We actively monitor and remove accounts that violate age restrictions</li>
        <li class="bullet-point">Age information is validated during profile creation and cannot be set below 18</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Content Moderation and Safety Measures</h2>

      <h3 class="sub-heading">Prohibited Content</h3>
      <p class="paragraph">We strictly prohibit any content that:</p>
      <ul class="bullet-list">
        <li class="bullet-point">Depicts, promotes, or facilitates child sexual abuse or exploitation</li>
        <li class="bullet-point">Involves minors in any sexual or inappropriate context</li>
        <li class="bullet-point">Contains or links to child sexual abuse material (CSAM)</li>
        <li class="bullet-point">Solicits or facilitates contact with minors for inappropriate purposes</li>
        <li class="bullet-point">Uses our platform to target, groom, or exploit minors</li>
      </ul>

      <h3 class="sub-heading">Content Monitoring</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Human Review:</b> Our moderation team reviews reported content and suspicious accounts</li>
        <li class="bullet-point"><b>Photo Moderation:</b> Profile photos are subject to review to ensure they comply with our community guidelines</li>
        <li class="bullet-point"><b>Real-time Monitoring:</b> We continuously monitor user activity and content for violations</li>
      </ul>

      <h3 class="sub-heading">User Reporting Mechanisms</h3>
      <p>Users can report concerning content or behavior through:</p>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Email Support:</b> Direct reporting via our support email</li>
        <li class="bullet-point"><b>Admin Dashboard:</b> Our administrative team actively monitors and responds to reports</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Reporting Child Sexual Abuse Material (CSAM)</h2>

      <h3 class="sub-heading">Immediate Reporting</h3>
      <p class="paragraph">If you encounter any content involving child exploitation or CSAM:</p>
      
      <h3 class="sub-heading">Report Immediately:</h3>
      <ul class="bullet-list">
        <li class="bullet-point">Contact our support team</li>
        <li class="bullet-point">Report to law enforcement authorities in your jurisdiction</li>
      </ul>

      <h3 class="sub-heading">Our Response Protocol</h3>
      <ol class="bullet-list">
        <li class="bullet-point">
          <strong>Immediate Action:</strong> Upon receiving a report of CSAM or child exploitation, we immediately:
          <ul>
            <li>Remove the reported content</li>
            <li>Suspend the reported account</li>
            <li>Preserve evidence for law enforcement</li>
          </ul>
        </li>
        <li class="bullet-point">
          <strong>Law Enforcement Cooperation:</strong> We:
          <ul>
            <li>Report suspected CSAM to the National Center for Missing & Exploited Children (NCMEC) and relevant authorities</li>
            <li>Cooperate fully with law enforcement investigations</li>
            <li>Preserve and provide evidence as required by law</li>
          </ul>
        </li>
        <li class="bullet-point">
          <strong>Transparency:</strong> We maintain records of all CSAM reports and our response actions
        </li>
      </ol>
    </section>

    <section class="section">
      <h2 class="heading">User Safety Features</h2>

      <h3 class="sub-heading">Account Security</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Phone Verification:</b> Required for all accounts</li>
        <li class="bullet-point"><b>Email Verification:</b> Required for all accounts</li>
        <li class="bullet-point"><b>Profile Completion:</b> Users must complete their profile with verified information before accessing full app features</li>
        <li class="bullet-point"><b>Active Account Monitoring:</b> We monitor account activity for suspicious behavior</li>
      </ul>

      <h3 class="sub-heading">Communication Safety</h3>
      <ul class="bullet-list">
         <li class="bullet-point"><b>Secure Messaging::</b> All communications between users are monitored for safety</li>
        <li class="bullet-point"><b>Block and Report:</b> Users can block and report other users at any time</li>
        <li class="bullet-point"><b>Privacy Controls:</b> Users have control over who can see their profile and contact them</li>
      
      </ul>

      <h3 class="sub-heading">Profile Verification</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Verified Accounts:</b> Users can opt for enhanced verification (KYC) to increase trust and safety</li>
        <li class="bullet-point"><b>Photo Requirements:</b> Profile photos must meet our community guidelines</li>
        <li class="bullet-point"><b>Profile Review:</b> Profiles are subject to review to ensure compliance with our terms</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Prevention Measures</h2>

      <h3 class="sub-heading">Technology and Tools</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Behavioral Analysis:</b> Monitoring of user behavior patterns to detect potential exploitation</li>
        <li class="bullet-point"><b>Account Suspension:</b> Immediate suspension of accounts that violate our policies</li>
      </ul>

      <h3 class="sub-heading">Education and Awareness</h3>
      <ul class="bullet-list">
        <li class="bullet-point"><b>Community Guidelines:</b> Clear guidelines about acceptable behavior and content</li>
        <li class="bullet-point"><b>Safety Resources:</b> Information and resources about online safety</li>
        <li class="bullet-point"><b>User Education:</b> Regular communication about safety best practices</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Compliance and Legal Obligations</h2>
      <h3 class="sub-heading">Legal Compliance</h3>
      <p class="paragraph">We comply with all applicable laws regarding child protection, including:</p>
      <ul class="bullet-list">
        <li class="bullet-point">The PROTECT Act (Prosecutorial Remedies and Other Tools to End the Exploitation of Children Today Act)</li>
        <li class="bullet-point">International child protection laws</li>
        <li class="bullet-point">Local jurisdictional requirements</li>
      </ul>

      <h3 class="sub-heading">Reporting to Authorities</h3>
      <h6>We report suspected CSAM to:</h6>
      <ul class="bullet-list">
        <li class="bullet-point">National Center for Missing & Exploited Children (NCMEC) - CyberTipline</li>
        <li class="bullet-point">Local law enforcement authorities</li>
        <li class="bullet-point">Relevant international organizations</li>
      </ul>

      <h3 class="sub-heading">Data Preservation</h3>
      <ul class="bullet-list">
        <li class="bullet-point">We preserve evidence related to CSAM reports as required by law</li>
        <li class="bullet-point">Evidence is maintained securely and provided to law enforcement upon request</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Contact and Support</h2>
      <h3 class="sub-heading">Reporting Child Safety Concerns</h3>
      <p class="paragraph">
        If you have concerns about child safety on our platform:
      </p>
      <ul class="bullet-list">
        <li class="bullet-point">
          Email: <strong>lamonte.scott@insizon.com</strong>
        </li>
        <li class="bullet-point">Subject Line: <strong>"CSAE Report - Urgent"</strong></li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Our Team and Training</h2>
      <h3 class="sub-heading">Dedicated Safety Team</h3>
      <ul class="bullet-list">
        <li class="bullet-point">We maintain a dedicated team focused on user safety and content moderation</li>
        <li class="bullet-point">Our team is trained to identify and respond to CSAE-related content and behavior</li>
        <li class="bullet-point">Regular training on child protection laws and best practices</li>
      </ul>

      <h3 class="sub-heading">Continuous Improvement</h3>
      <ul class="bullet-list">
        <li class="bullet-point">We regularly review and update our safety measures</li>
        <li class="bullet-point">We stay informed about emerging threats and adapt our systems accordingly</li>
        <li class="bullet-point">We collaborate with industry partners and safety organizations</li>
      </ul>
    </section>

    <section class="section">
      <h2 class="heading">Transparency and Accountability</h2>
      <h3 class="sub-heading">Regular Reviews</h3>
      <ul class="bullet-list">
        <li class="bullet-point">We conduct regular audits of our safety measures</li>
        <li class="bullet-point">We review and update this policy as needed</li>
        <li class="bullet-point">We publish updates to our safety practices</li>
      </ul>

      <h3 class="sub-heading">User Communication</h3>
      <ul class="bullet-list">
        <li class="bullet-point">We communicate safety updates to our user community</li>
        <li class="bullet-point">We provide clear information about reporting mechanisms</li>
        <li class="bullet-point">We maintain transparency about our safety measures</li>
      </ul>
      <p class="paragraph">
        <em>This policy reflects our commitment to creating a safe environment for all users. We take our responsibility to protect minors seriously and continuously invest in technology, processes, and partnerships to prevent child sexual abuse and exploitation.</em>
      </p>
    </section>
  </div>
</div>
